import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  AlertCircle,
  Loader2,
  Bot,
  AlertTriangle,
  FileCheck2,
  CalendarClock,
  Mail,
  Lock,
  User,
  Eye,
  EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

const BRAND_CYAN = '#00A1DF';
const BRAND_CYAN_DARK = '#0089BE';
const BRAND_GRAY_DARK = '#5C5D60';
const BRAND_FONT = "'Myriad Pro', 'PT Sans', 'Segoe UI', sans-serif";

const FEATURES = [
  { icon: Bot, text: 'Agente IA sobre stock, ventas, compras y tickets' },
  { icon: AlertTriangle, text: 'Diagnóstico de quiebres con causas y acciones' },
  { icon: FileCheck2, text: 'Aprobaciones, cobranzas, OC y comprobantes en un solo lugar' },
  { icon: CalendarClock, text: 'Tareas programadas y paneles que trabajan por vos' }
];

const LoginPage = () => {
  const { login, register } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Sesión iniciada correctamente');
      } else {
        if (formData.password !== formData.confirmPassword) {
          setError('Las contraseñas no coinciden');
          setLoading(false);
          return;
        }
        await register(formData.name, formData.email, formData.password);
        toast.success('Cuenta creada correctamente');
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Error al procesar la solicitud';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  return (
    <div className="min-h-screen flex" style={{ fontFamily: BRAND_FONT }} data-testid="login-page">
      {/* Left side - SUR Branding */}
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{ backgroundColor: BRAND_CYAN }}
      >
        <div className="relative z-10 flex flex-col justify-between px-12 py-10 w-full">
          {/* Logo */}
          <div>
            <img
              src="/logo-sur-blanco.png"
              alt="Sur"
              className="h-20 w-auto"
            />
            <p className="text-sm mt-3 text-white/80">
              Centro integral de higiene para empresas e instituciones
            </p>
          </div>

          {/* Main message + features */}
          <div className="my-8">
            <h1 className="text-4xl font-bold text-white mb-3 leading-tight">
              Preguntá. Diagnosticá. Automatizá.
            </h1>
            <p className="text-lg text-white/90 max-w-md mb-6">
              Tu operación conectada: un agente de IA que analiza tus datos y
              te da respuestas al instante, automatización de las tareas
              repetitivas de todos los días y trazabilidad completa de cada
              documento cargado.
            </p>

            <div className="space-y-2 max-w-md">
              {FEATURES.map(({ icon: Icon, text }) => (
                <div
                  key={text}
                  className="flex items-center gap-3 rounded-lg px-4 py-2.5 bg-white shadow-sm"
                >
                  <Icon className="h-5 w-5 shrink-0" style={{ color: BRAND_CYAN }} />
                  <span className="text-sm font-medium" style={{ color: BRAND_GRAY_DARK }}>{text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Quote + claim */}
          <div className="border-l-4 border-white pl-4 max-w-md">
            <p className="text-white italic leading-relaxed">
              “La mejor operación no es la que resuelve los problemas rápido:
              es la que los ve venir.”
            </p>
            <p className="text-white/80 font-semibold text-sm mt-2">
              Somos Sur, Somos Higiene
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#FAFBFC]">
        <Card className="w-full max-w-md border-border/50 shadow-lg" data-testid="login-card">
          <CardHeader className="space-y-1">
            {/* Mobile logo */}
            <div
              className="flex items-center gap-3 lg:hidden mb-4 rounded-xl px-4 py-3"
              style={{ backgroundColor: BRAND_CYAN }}
            >
              <img
                src="/logo-sur-blanco.png"
                alt="Sur"
                className="h-10 w-auto"
              />
            </div>
            <CardTitle className="text-2xl font-bold">
              {isLogin ? 'Iniciar sesión' : 'Crear cuenta'}
            </CardTitle>
            <CardDescription>
              {isLogin
                ? 'Accedé al sistema con tus credenciales'
                : 'Completá los datos para registrarte'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre completo</Label>
                  <div className="relative">
                    <User
                      className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4"
                      style={{ color: BRAND_CYAN }}
                    />
                    <Input
                      id="name"
                      name="name"
                      placeholder="Juan Pérez"
                      value={formData.name}
                      onChange={handleInputChange}
                      required={!isLogin}
                      className="h-12 pl-10 rounded-xl"
                      data-testid="register-name-input"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail
                    className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4"
                    style={{ color: BRAND_CYAN }}
                  />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="usuario@cdsurargentina.com.ar"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="h-12 pl-10 rounded-xl"
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Contraseña</Label>
                <div className="relative">
                  <Lock
                    className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4"
                    style={{ color: BRAND_CYAN }}
                  />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    minLength={6}
                    className="h-12 pl-10 pr-10 rounded-xl"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowPassword(prev => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirmar contraseña</Label>
                  <div className="relative">
                    <Lock
                      className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4"
                      style={{ color: BRAND_CYAN }}
                    />
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      required={!isLogin}
                      minLength={6}
                      className="h-12 pl-10 rounded-xl"
                      data-testid="register-confirm-password-input"
                    />
                  </div>
                </div>
              )}

              {error && (
                <div className="flex items-center gap-2 text-destructive text-sm p-3 bg-destructive/10 rounded-md" data-testid="login-error">
                  <AlertCircle className="h-4 w-4" />
                  {error}
                </div>
              )}

              <Button
                type="submit"
                className="w-full h-12 rounded-xl text-white"
                style={{ backgroundColor: BRAND_CYAN }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = BRAND_CYAN_DARK; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = BRAND_CYAN; }}
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isLogin ? 'Ingresar' : 'Crear cuenta'}
              </Button>

              <div className="text-center text-sm">
                <span className="text-muted-foreground">
                  {isLogin ? '¿No tenés cuenta?' : '¿Ya tenés cuenta?'}
                </span>{' '}
                <button
                  type="button"
                  onClick={() => {
                    setIsLogin(!isLogin);
                    setError('');
                  }}
                  className="font-medium hover:underline"
                  style={{ color: BRAND_CYAN }}
                  data-testid="toggle-auth-mode-btn"
                >
                  {isLogin ? 'Registrate' : 'Iniciá sesión'}
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
